import { useEffect, useState, useCallback } from "react";
import { api, downloadReport, formatDate } from "@/lib/api";
import { Check, X, Bell, Printer, Search } from "lucide-react";
import { toast } from "sonner";

export default function Presenze() {
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [assenze, setAssenze] = useState([]);
  const [sezione, setSezione] = useState("");
  const [sezioni, setSezioni] = useState([]);
  const [ricerca, setRicerca] = useState("");

  const load = useCallback(() => {
    api.get("/presenze", { params: { data, ...(sezione ? { sezione } : {}) } }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/assenze").then((r) => setAssenze(r.data)).catch(() => {});
  }, [data, sezione]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/impostazioni").then(({data}) => setSezioni((data.sezioni || []).filter(Boolean))).catch(() => {}); }, []);

  const segna = async (bimboId, stato) => {
    try {
      await api.put(`/presenze/${bimboId}`, { data, stato });
      load();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const segnaLetta = async (a) => {
    await api.put(`/assenze/${a.id}/letta`);
    load();
  };

  const termineRicerca = ricerca.trim().toLowerCase();
  const rowsFiltrate = rows.filter((r) => !termineRicerca || [r.bimbo?.nome, r.bimbo?.cognome, r.bimbo?.sezione, r.bimbo?.genitore_cf]
    .filter(Boolean).join(" ").toLowerCase().includes(termineRicerca));
  const presenti = rowsFiltrate.filter((r) => r.stato === "presente").length;
  const assenzeDelGiorno = assenze.filter((a) =>
    (!a.data_inizio || data >= a.data_inizio) &&
    (!a.data_fine || data <= a.data_fine) &&
    (!sezione || a.bimbo_sezione === sezione) &&
    (!termineRicerca || [a.bimbo_nome, a.bimbo_sezione].filter(Boolean).join(" ").toLowerCase().includes(termineRicerca))
  );

  return (
    <div className="space-y-6 animate-fade-up" data-testid="presenze-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#3D405B] tracking-tight">Presenze</h1>
          <p className="text-[#6C757D] mt-1 text-sm" data-testid="presenze-count">{presenti} presenti su {rowsFiltrate.length} iscritti</p>
        </div>
        <div className="flex flex-wrap gap-2"><button onClick={()=>downloadReport("presenze",{data,...(sezione?{filtro:sezione}:{})})} className="btn-sage"><Printer className="w-4 h-4"/> Stampa PDF</button><select aria-label="Filtra sezione" value={sezione} onChange={(e)=>setSezione(e.target.value)} className="input-warm !w-auto"><option value="">Tutte le sezioni</option>{sezioni.map((s)=><option key={s} value={s}>{s}</option>)}</select><input aria-label="Filtra mese" type="month" value={mese} onChange={(e) => { setMese(e.target.value); setData(`${e.target.value}-01`); }} className="input-warm !w-auto"/><input type="date" min={`${mese}-01`} max={`${mese}-31`} value={data} onChange={(e) => {setData(e.target.value);setMese(e.target.value.slice(0,7));}} data-testid="presenze-date-input" className="input-warm !w-auto" /></div>
      </div>

      <div className="card-warm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a89a8a]" />
          <input value={ricerca} onChange={(e) => setRicerca(e.target.value)} className="input-warm !pl-10"
            placeholder="Cerca bambino per nome, cognome o sezione..." aria-label="Cerca nelle presenze" />
        </div>
      </div>

      <div className="card-warm overflow-hidden">
        <div className="divide-y divide-[#faf3ea]" data-testid="presenze-list">
          {rowsFiltrate.map((r) => (
            <div key={r.bimbo.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-4 hover:bg-[#FAF6F0] transition" data-testid={`presenza-row-${r.bimbo.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-semibold text-white ${r.bimbo.sezione === "nido" ? "bg-[#F4A261]" : "bg-[#81B29A]"}`}>
                  {r.bimbo.nome[0]}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-[#3D405B] text-sm break-words">{r.bimbo.nome} {r.bimbo.cognome}</p>
                  <p className="text-xs text-[#a89a8a]">{r.bimbo.sezione || "Senza sezione"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
                <button onClick={() => segna(r.bimbo.id, "presente")} data-testid={`presente-${r.bimbo.id}`}
                  className={`min-w-0 flex items-center justify-center gap-1 rounded-full px-2 sm:px-4 py-2.5 text-xs font-bold transition ${r.stato === "presente" ? "bg-[#81B29A] text-white shadow" : "bg-[#E8F3EE] text-[#4a7a63] hover:bg-[#d8ebe2]"}`}>
                  <Check className="w-3.5 h-3.5" /> Presente
                </button>
                <button onClick={() => segna(r.bimbo.id, "assente")} data-testid={`assente-${r.bimbo.id}`}
                  className={`min-w-0 flex items-center justify-center gap-1 rounded-full px-2 sm:px-4 py-2.5 text-xs font-bold transition ${r.stato === "assente" ? "bg-red-500 text-white shadow" : "bg-red-50 text-red-500 hover:bg-red-100"}`}>
                  <X className="w-3.5 h-3.5" /> Assente
                </button>
              </div>
            </div>
          ))}
          {rowsFiltrate.length === 0 && <p className="px-5 py-10 text-center text-[#a89a8a]" data-testid="presenze-empty">{ricerca ? "Nessun bambino corrisponde alla ricerca." : "Nessun bimbo iscritto."}</p>}
        </div>
      </div>

      <div>
        <h2 className="font-heading text-2xl font-semibold text-[#3D405B] mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#E07A5F]" /> Comunicazioni assenza dai genitori
        </h2>
        <div className="space-y-3" data-testid="assenze-list">
          {assenzeDelGiorno.length === 0 && <p className="text-sm text-[#a89a8a]" data-testid="assenze-empty">Nessuna assenza comunicata dai genitori per il {formatDate(data)}.</p>}
          {assenzeDelGiorno.map((a) => (
            <div key={a.id} className={`card-warm p-4 flex flex-wrap items-center justify-between gap-3 ${a.letta ? "opacity-60" : ""}`} data-testid={`assenza-${a.id}`}>
              <div>
                <p className="font-semibold text-sm text-[#3D405B]">{a.bimbo_nome}</p>
                <p className="text-xs text-[#6C757D]">dal {formatDate(a.data_inizio)} al {formatDate(a.data_fine)} · {a.motivo}</p>
              </div>
              {!a.letta ? (
                <button onClick={() => segnaLetta(a)} data-testid={`letta-${a.id}`} className="btn-sage !px-4 !py-1.5 text-xs">Segna come letta</button>
              ) : (
                <span className="text-xs font-semibold text-[#4a7a63]">Letta</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
